import { Alert, Grid } from "@mui/material";
import React from "react";
import { TaskDataRowDataType, TaskRepetitionType, TaskTypeClassWidthPrefix } from "ui/Scripts/source/models/enums";
import { ITaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { Translations } from "ui/Scripts/source/models/translations";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard";
import { TaskTypeDialogPreviewType } from "../TaskTypeDialogPreviewWeb";
import { ILineColumnWidth } from "../../TaskTypeDialogFormDesigner/FormDesignerComponentProperties/FormDesignerComponentPropertiesRow";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { FormDesignerComponentPropertiesWidth } from "../../TaskTypeDialogFormDesigner/FormDesignerComponentProperties/FormDesignerComponentPropertiesWidth";
import { TaskTypeDialogPreviewComponentPropertiesRow } from "../TaskTypeDialogPreviewComponentPropertiesRow";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { FormAccordion } from "source/components/framework/FormAccordion";
import { FormDesignerComponentPropertiesName } from "../../TaskTypeDialogFormDesigner/FormDesignerComponentProperties/FormDesignerComponentPropertiesName";
import { ITaskTypeFieldPropUpdate } from "../../FormDesignerTypes";

interface ITaskTypeDialogPreviewComponentPropertiesProps {
    previewType: TaskTypeDialogPreviewType;
    taskRepetitionType: TaskRepetitionType;
    taskType: ITaskEditTypeItem;
    headerTypeCol?: ITaskTypeCol;
    formTypeCol?: ITaskTypeCol;
    lineTypeCol?: ITaskTypeCol;
    lineColumnWidths: ILineColumnWidth[];
    onHeaderItemClick: (field: string) => void;
    onFormItemClick: (field: string) => void;
    onTaskTypeHeaderPropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
    onTaskTypeFormPropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
    onChangeLineColumnWidth: (id: string, classes: string) => void;
}

export const TaskTypeDialogPreviewComponentProperties = (
    props: ITaskTypeDialogPreviewComponentPropertiesProps
) => {
    const taskTypeCol = props.headerTypeCol ?? props.formTypeCol ?? props.lineTypeCol;

    const isLabel = taskTypeCol?.dataType === TaskDataRowDataType.Label;

    const isLineBreak = taskTypeCol?.dataType === TaskDataRowDataType.LineBreak;

    const isHeaderField = !!props.headerTypeCol;

    const onTaskTypePropUpdate = (values: ITaskTypeFieldPropUpdate[]) => {
        if (props.headerTypeCol) {
            props.onTaskTypeHeaderPropUpdate(values);
        } else {
            props.onTaskTypeFormPropUpdate(values);
        }
    };

    const handleWidthChange = (field: string, classes: string) => {
        onTaskTypePropUpdate([{ field, name: "class", value: classes }]);
    };

    const formFieldMargin = ".5rem 0 0 0";

    return (
        <DialogSectionCard headerTitle={Translations.FormProperties} contentPadding="16px 0 16px 0!important">
            <Grid sx={{
                overflowY: "auto",
                overflowX: "hidden",
                height: "calc(100vh - 350px)",
                paddingLeft: "0",
                paddingRight: "0"
            }}>
                {(!taskTypeCol
                    ? <div>
                        <Alert severity="info">{Translations.SelectComponentInFormToSeeComponentProperties}</Alert>
                    </div>
                    : <FormDesignerComponentPropertiesName
                        headerTypeCol={props.headerTypeCol}
                        formTypeCol={props.formTypeCol}
                        headerCols={props.taskType.headerCols}
                        formCols={props.taskType.formCols}
                        onHeaderItemClick={props.onHeaderItemClick}
                        onFormItemClick={props.onFormItemClick}
                    />
                )}
                {(!!props.headerTypeCol || !!props.formTypeCol) &&
                    <>
                        {(!isLineBreak && !(!isHeaderField && props.taskRepetitionType === TaskRepetitionType.Repeated && props.previewType === TaskTypeDialogPreviewType.Print)) &&
                            <FormAccordion
                                defaultExpanded={true}
                                title={Translations.BasicInformation}
                            >
                                <GridRow>
                                    {props.previewType === TaskTypeDialogPreviewType.Web &&
                                        <Grid item sm={12}>
                                            <FormDesignerComponentPropertiesWidth
                                                classes={taskTypeCol.class}
                                                formFieldMargin={0}
                                                sizePrefix={
                                                    taskTypeCol.class.includes(TaskTypeClassWidthPrefix.Web2)
                                                        ? TaskTypeClassWidthPrefix.Web2
                                                        : TaskTypeClassWidthPrefix.Web
                                                }
                                                id={taskTypeCol.field}
                                                title={Translations.FormWidth}
                                                minimum={0}
                                                previewMode={true}
                                                onUpdate={handleWidthChange}
                                            />
                                        </Grid>
                                    }
                                    {props.previewType === TaskTypeDialogPreviewType.Mobile &&
                                        <Grid item sm={12}>
                                            <FormDesignerComponentPropertiesWidth
                                                classes={taskTypeCol.class}
                                                formFieldMargin={0}
                                                sizePrefix={TaskTypeClassWidthPrefix.Mobile}
                                                id={taskTypeCol.field}
                                                title={Translations.FormWidthMobile}
                                                minimum={0}
                                                onUpdate={handleWidthChange}
                                            />
                                        </Grid>
                                    }
                                    {(props.previewType === TaskTypeDialogPreviewType.Print || props.previewType === TaskTypeDialogPreviewType.PrintRow) &&
                                        <Grid item sm={12}>
                                            <FormDesignerComponentPropertiesWidth
                                                classes={taskTypeCol.class}
                                                formFieldMargin={0}
                                                sizePrefix={TaskTypeClassWidthPrefix.Print}
                                                id={taskTypeCol.field}
                                                title={Translations.FormWidthPrint}
                                                minimum={0}
                                                onUpdate={handleWidthChange}
                                            />
                                        </Grid>
                                    }
                                </GridRow>
                            </FormAccordion>
                        }
                    </>
                }
                {((!!props.formTypeCol || !!props.lineTypeCol) && !isLabel && !isLineBreak && props.previewType !== TaskTypeDialogPreviewType.PrintRow) &&
                    <TaskTypeDialogPreviewComponentPropertiesRow
                        previewType={props.previewType}
                        formFieldMargin={formFieldMargin}
                        lineTypeCol={props.lineTypeCol}
                        lineColumnWidths={props.lineColumnWidths}
                        onChangeLineColumnWidth={props.onChangeLineColumnWidth}
                    />
                }
            </Grid>
        </DialogSectionCard>
    );
};
